import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { StayingOnTicks } from "layouts/shows/confirm-dates/utils/stayingOn";

interface SelectHorsesState {
  // Maps horse microchip to selection state
  selectedItems: Record<string, boolean>;
  // Maps horse microchip to state (location)
  horseStateMap: Record<string, string>;
  // Maps horse microchip to county
  horseCountyMap: Record<string, string>;
  // Default state for all horses
  defaultState: string;
  // Default county for all horses
  defaultCounty: string;
  // Staying-on pop-up ticks, keyed `${microchip}|${earlierEventId}|${laterEventId}`
  stayingOnTicks: StayingOnTicks;
}

const initialState: SelectHorsesState = {
  selectedItems: {},
  horseStateMap: {},
  horseCountyMap: {},
  defaultState: "",
  defaultCounty: "",
  stayingOnTicks: {},
};

const selectHorsesSlice = createSlice({
  name: "selectHorses",
  initialState,
  reducers: {
    // Selected Items Actions
    setSelectedItems: (state, action: PayloadAction<Record<string, boolean>>) => {
      state.selectedItems = action.payload;
    },
    toggleSelectedItem: (state, action: PayloadAction<{ itemId: string; checked: boolean }>) => {
      const { itemId, checked } = action.payload;
      if (checked) {
        state.selectedItems[itemId] = true;
      } else {
        delete state.selectedItems[itemId];
      }
    },
    selectAllItems: (state, action: PayloadAction<string[]>) => {
      state.selectedItems = action.payload.reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {} as Record<string, boolean>);
    },
    unselectAllItems: (state) => {
      state.selectedItems = {};
    },

    // Horse State Map Actions
    setHorseState: (state, action: PayloadAction<{ microchip: string; stateValue: string }>) => {
      const { microchip, stateValue } = action.payload;
      state.horseStateMap[microchip] = stateValue;
      // Clear county when state changes
      delete state.horseCountyMap[microchip];
    },
    setHorseStateMap: (state, action: PayloadAction<Record<string, string>>) => {
      state.horseStateMap = action.payload;
    },
    clearHorseState: (state, action: PayloadAction<string>) => {
      delete state.horseStateMap[action.payload];
      delete state.horseCountyMap[action.payload];
    },

    // Horse County Map Actions
    setHorseCounty: (state, action: PayloadAction<{ microchip: string; county: string }>) => {
      const { microchip, county } = action.payload;
      state.horseCountyMap[microchip] = county;
    },
    setHorseCountyMap: (state, action: PayloadAction<Record<string, string>>) => {
      state.horseCountyMap = action.payload;
    },
    clearHorseCounty: (state, action: PayloadAction<string>) => {
      delete state.horseCountyMap[action.payload];
    },

    // Default State Actions
    setDefaultState: (state, action: PayloadAction<string>) => {
      state.defaultState = action.payload;
      // Clear default county when default state changes
      state.defaultCounty = "";
      // Clear own counties of horses that inherit the default state (incl. rows on other pages)
      for (const microchip of Object.keys(state.horseCountyMap)) {
        if (!(microchip in state.horseStateMap)) {
          delete state.horseCountyMap[microchip];
        }
      }
    },

    // Default County Actions
    setDefaultCounty: (state, action: PayloadAction<string>) => {
      state.defaultCounty = action.payload;
    },

    setStayingOnTicks: (state, action: PayloadAction<StayingOnTicks>) => {
      state.stayingOnTicks = action.payload;
    },

    // Reset all state
    resetSelectHorses: () => initialState,
  },
  selectors: {
    selectSelectedItems: (sliceState) => sliceState.selectedItems,
    selectSelectedItemsCount: (sliceState) =>
      Object.values(sliceState.selectedItems).filter(Boolean).length,
    selectHorseStateMap: (sliceState) => sliceState.horseStateMap,
    selectHorseCountyMap: (sliceState) => sliceState.horseCountyMap,
    selectDefaultState: (sliceState) => sliceState.defaultState,
    selectDefaultCounty: (sliceState) => sliceState.defaultCounty,
    selectStayingOnTicks: (sliceState) => sliceState.stayingOnTicks,
  },
});

export const {
  setSelectedItems,
  toggleSelectedItem,
  selectAllItems,
  unselectAllItems,
  setHorseState,
  setHorseStateMap,
  clearHorseState,
  setHorseCounty,
  setHorseCountyMap,
  clearHorseCounty,
  setDefaultState,
  setDefaultCounty,
  resetSelectHorses,
  setStayingOnTicks,
} = selectHorsesSlice.actions;

export const {
  selectSelectedItems,
  selectSelectedItemsCount,
  selectHorseStateMap,
  selectHorseCountyMap,
  selectDefaultState,
  selectDefaultCounty,
  selectStayingOnTicks,
} = selectHorsesSlice.selectors;

export default selectHorsesSlice.reducer;
